import React from 'react';
import { Loader2, MoveRight } from 'lucide-react';

import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Label } from '../../../components/ui/label';
import { SearchableSelect } from '../../../components/ui/SearchableSelect';

export interface MovePipesDialogPipe {
  id: number | string;
  pipeID: string;
}

interface MovePipesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pipes: MovePipesDialogPipe[];
  targetOptions: Array<{ id: string | number; name: string }>;
  targetName: string;
  onTargetNameChange: (name: string) => void;
  targetDisplayName?: string;
  moveAlreadyOnTarget: boolean;
  isMoving: boolean;
  onConfirm: () => void;
}

export const MovePipesDialog: React.FC<MovePipesDialogProps> = ({
  open,
  onOpenChange,
  pipes,
  targetOptions,
  targetName,
  onTargetNameChange,
  targetDisplayName,
  moveAlreadyOnTarget,
  isMoving,
  onConfirm,
}) => {
  const isBulk = pipes.length > 1;
  const hasTarget = Boolean(targetName.trim());
  const canMove = hasTarget && !moveAlreadyOnTarget && !isMoving;

  return (
    <Dialog open={open} onOpenChange={(next) => !isMoving && onOpenChange(next)}>
      <DialogContent className="bg-white max-w-md w-full">
        <DialogHeader className="pl-0 pr-14 pb-4">
          <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight flex items-center gap-2">
            <MoveRight className="h-5 w-5 shrink-0" aria-hidden />
            Move pipe
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {isBulk ? (
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-[13px] text-heritage-grey mb-2">
                {pipes.length} pipe{pipes.length !== 1 ? 's' : ''} selected:
              </p>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {pipes.map((pipe) => (
                  <div
                    key={String(pipe.id ?? pipe.pipeID)}
                    className="text-sm text-gray-700 py-1 flex items-center"
                  >
                    <span className="w-2 h-2 bg-gray-400 rounded-full mr-2 flex-shrink-0" />
                    Pipe ID: {pipe.pipeID || '—'}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-[13px] text-heritage-grey">
              Pipe{' '}
              <span className="font-semibold text-text-grey">{pipes[0]?.pipeID || '—'}</span>
            </p>
          )}

          <div className="space-y-2">
            <Label className="text-[13px] font-semibold text-blueprint-blue">
              Target pipe size
            </Label>
            <SearchableSelect
              value={targetName}
              onValueChange={onTargetNameChange}
              placeholder="Select pipe size"
              searchPlaceholder="Search pipe size..."
              options={targetOptions}
              showAddButton={false}
              disabled={isMoving}
              className="w-full"
            />
            {moveAlreadyOnTarget && hasTarget ? (
              <p className="text-[12px] text-readable-red">
                Selected pipes are already on this pipe size.
              </p>
            ) : (
              <p className="text-[12px] text-heritage-grey">
                Same pipe size and wall thickness only. Group ID will be replaced
                {targetDisplayName ? (
                  <>
                    {' '}
                    when moving to{' '}
                    <span className="font-medium text-text-grey">{targetDisplayName}</span>.
                  </>
                ) : (
                  '.'
                )}
              </p>
            )}
          </div>
        </div>
        <DialogFooter className="gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isMoving}
            className="min-w-[100px]"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={!canMove}
            className="bg-hero-red hover:bg-hero-red/90 text-white min-w-[100px]"
          >
            {isMoving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Moving…
              </>
            ) : (
              'Move'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
